import React, { useState, useEffect } from 'react';
import { Tag, Check, Loader2, Send, X, Gift, Clock, Star, ChevronRight, Zap } from 'lucide-react';

const PromoCodeSection = ({
  showPromoCodeAlert,
  setShowPromoCodeAlert,
  promoCodeInput,
  setPromoCodeInput,
  promoCodeLoading,
  promoCodeMessage,
  handlePromoCodeSubmit,
  activePromoCodes,
  formatDate,
  setShowPromoRequest,
  showPromoRequest,
  promoRequestLoading,
  promoRequestMessage,
  setPromoRequestMessage,
  handlePromoRequest,
  user,
  promoRequestStatus,
  totalPromoUsers // 실제 프로모션 사용자 수
}) => {
  // 프로모션 요청 폼 상태
  const [promoRequestForm, setPromoRequestForm] = useState({
    gender: '',
    ageGroup: '',
    usageReasons: [],
    otherReason: '',
    discoveryPath: '',
    otherPath: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [timeLeft, setTimeLeft] = useState('');
  
  // 카운트다운 타이머
  useEffect(() => {
    const calculateTimeLeft = () => {
      const endDate = new Date('2025-08-31'); // 프로모션 종료일
      const now = new Date();
      const difference = endDate - now;
      
      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
        setTimeLeft(`${days}일 ${hours}시간`);
      } else {
        setTimeLeft('종료');
      }
    };
    
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000 * 60); // 1분마다 업데이트
    
    return () => clearInterval(timer);
  }, []);
  
  const getBenefitText = (promoCode) => {
    const durationDays = promoCode?.promo_codes?.benefit_duration_days || 180;
    return `${durationDays}일간 유료 기능 사용 가능`;
  };

  // 사용 이유 체크박스 핸들러
  const handleUsageReasonChange = (reason) => {
    setPromoRequestForm(prev => ({
      ...prev,
      usageReasons: prev.usageReasons.includes(reason)
        ? prev.usageReasons.filter(r => r !== reason)
        : [...prev.usageReasons, reason]
    }));
  };

  // 폼 유효성 검사
  const validateForm = () => {
    const errors = {};
    
    if (!promoRequestForm.gender) {
      errors.gender = '성별을 선택해주세요.';
    }
    
    if (!promoRequestForm.ageGroup) {
      errors.ageGroup = '연령대를 선택해주세요.';
    }
    
    if (promoRequestForm.usageReasons.length === 0) {
      errors.usageReasons = '사용 목적을 하나 이상 선택해주세요.';
    }
    
    if (promoRequestForm.usageReasons.includes('기타') && !promoRequestForm.otherReason.trim()) {
      errors.otherReason = '기타 사용 이유를 입력해주세요.';
    }
    
    if (!promoRequestForm.discoveryPath) {
      errors.discoveryPath = '라떼이즈를 알게 된 경로를 선택해주세요.';
    }
    
    if (promoRequestForm.discoveryPath === '기타' && !promoRequestForm.otherPath.trim()) {
      errors.otherPath = '기타 경로를 입력해주세요.';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 폼이 유효한지 실시간으로 확인
  const isFormValid = () => {
    if (!promoRequestForm.gender) return false;
    if (!promoRequestForm.ageGroup) return false;
    if (promoRequestForm.usageReasons.length === 0) return false;
    if (promoRequestForm.usageReasons.includes('기타') && !promoRequestForm.otherReason.trim()) return false;
    if (!promoRequestForm.discoveryPath) return false;
    if (promoRequestForm.discoveryPath === '기타' && !promoRequestForm.otherPath.trim()) return false;
    return true;
  };

  // 프로모션 요청 핸들러 수정
  const handlePromoRequestSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    await handlePromoRequest(promoRequestForm);
  };

  return (
    <>
      <div className="space-y-6">
        {/* 프로모션 코드 필요 알림 - 강화된 버전 */}
        {showPromoCodeAlert && (
          <div className="relative overflow-hidden bg-gradient-to-r from-amber-600 to-orange-600 rounded-2xl p-6 shadow-2xl animate-pulse-slow">
            {/* 배경 패턴 */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
              }}></div>
            </div>
            
            <div className="relative z-10">
              <div className="max-w-4xl mx-auto">
                  {/* 헤더 섹션 */}
                  <div className="mb-4">
                    <h3 className="text-lg md:text-xl font-bold text-white">
                      🎁 지금만 한정! 3개월 무료 이용 기회
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <Clock className="h-4 w-4 text-amber-200" />
                      <span className="text-amber-200 font-semibold text-sm">
                        프로모션 종료까지: {timeLeft}
                      </span>
                    </div>
                  </div>
                  
                  {/* 메인 메시지 */}
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 mb-4">
                    <p className="text-white font-semibold mb-2">
                      ⚡ 곧 유료 전환 예정! 지금이 마지막 기회입니다
                    </p>
                    <p className="text-white/90 text-sm mb-3">
                      라떼이즈는 현재 베타 서비스 기간으로 무료 프로모션을 제공하고 있습니다.
                      정식 유료화 전, 지금 신청하시면 <span className="font-bold text-amber-200">3개월간 모든 프리미엄 기능</span>을 무료로 이용하실 수 있습니다!
                    </p>
                    <div className="bg-amber-900/30 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-white/90 text-sm">Pro 플랜 3개월 정상가</span>
                        <span className="text-white/70 line-through">₩29,700</span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-amber-200 font-bold">프로모션 특별가</span>
                        <div className="flex items-center gap-2">
                          <span className="text-amber-200 font-bold text-xl">₩0</span>
                          <div className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                            100% OFF
                          </div>
                        </div>
                      </div>
                      <div className="text-center mt-2 pt-2 border-t border-white/20">
                        <span className="text-white font-bold text-lg">💰 ₩29,700 절약!</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* 혜택 리스트 */}
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <div className="flex items-center gap-2 text-white/90">
                      <Star className="h-4 w-4 text-amber-200 flex-shrink-0" />
                      <span className="text-xs md:text-sm">AI 이력서 무제한 생성</span>
                    </div>
                    <div className="flex items-center gap-2 text-white/90">
                      <Star className="h-4 w-4 text-yellow-300 flex-shrink-0" />
                      <span className="text-xs md:text-sm">이력서 PDF 다운로드</span>
                    </div>
                    <div className="flex items-center gap-2 text-white/90">
                      <Star className="h-4 w-4 text-yellow-300 flex-shrink-0" />
                      <span className="text-xs md:text-sm">영문 이력서 자동 생성</span>
                    </div>
                    <div className="flex items-center gap-2 text-white/90">
                      <Star className="h-4 w-4 text-yellow-300 flex-shrink-0" />
                      <span className="text-xs md:text-sm">개인 웹사이트 생성</span>
                    </div>
                  </div>
                  
                  {/* 소셜 증명 */}
                  <div className="w-full bg-amber-400/20 backdrop-blur-sm rounded-lg px-4 py-2 inline-flex items-center gap-2 mb-4">
                    <Zap className="h-4 w-4 text-amber-200" />
                    <span className="text-white text-sm font-medium">
                      이미 {(totalPromoUsers || 26) * 33}명이 프로모션 혜택을 받았습니다!
                    </span>
                  </div>
                  
                  {/* CTA 버튼 */}
                  <button
                    onClick={() => {
                      setShowPromoRequest(true);
                    }}
                    className="w-full inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 text-gray-900 font-bold py-3 px-6 rounded-lg transition-all transform hover:scale-105 shadow-lg animate-pulse"
                  >
                    <Gift className="h-5 w-5" />
                    지금 바로 무료 프로모션 신청하기
                    <ChevronRight className="h-5 w-5 animate-bounce-x" />
                  </button>
              </div>
            </div>
          </div>
        )}
        
        {/* 프로모션 코드 입력 - 활성 프로모션 코드가 없을 때만 표시 */}
        {activePromoCodes.length === 0 && (
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">프로모션 코드</h3>
            
            <div className="space-y-4">
              <div>
                <label className="label">프로모션 코드 입력</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={promoCodeInput}
                    onChange={(e) => setPromoCodeInput(e.target.value.toUpperCase())}
                    onKeyPress={(e) => e.key === 'Enter' && handlePromoCodeSubmit()}
                    placeholder="코드를 입력하세요"
                    className="input flex-1"
                    disabled={promoCodeLoading}
                  />
                  <button
                    onClick={handlePromoCodeSubmit}
                    disabled={promoCodeLoading || !promoCodeInput.trim()}
                    className="btn btn-primary btn-md"
                  >
                    {promoCodeLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    적용
                  </button>
                </div>
                
                {promoCodeMessage.text && (
                  <div className={`mt-2 text-sm ${
                    promoCodeMessage.type === 'success' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {promoCodeMessage.text}
                  </div>
                )}
              </div>

              <div className="text-xs text-gray-500">
                <p>• 프로모션 코드를 통해 추가 기능을 이용할 수 있습니다.</p>
                <p>• 대소문자를 구분하지 않습니다.</p>
                <p>• 이미 사용한 코드는 재사용할 수 없습니다.</p>
              </div>

              {/* 프로모션 코드 요청 버튼 */}
              <div className="pt-4 border-t">
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-4 mb-3">
                  <p className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                    <Gift className="h-4 w-4 text-amber-600" />
                    아직 프로모션 코드가 없으신가요?
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    지금 신청하면 <span className="font-bold">₩29,700 상당</span>의 3개월 무료 이용 코드를 받을 수 있습니다!
                  </p>
                  <div className="mt-2 bg-white/50 rounded px-2 py-1 inline-block">
                    <span className="text-xs text-amber-900">Pro 플랜 월 ₩9,900 × 3개월 = <strong>₩29,700 절약!</strong></span>
                  </div>
                </div>
                {promoRequestStatus === 'pending' ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-blue-800">
                      프로모션 코드 승인을 대기 중입니다.
                    </p>
                    <p className="text-sm text-blue-700 mt-1">
                      승인 후 이메일로 코드가 발송됩니다.
                    </p>
                  </div>
                ) : promoRequestStatus === 'rejected' ? (
                  <>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-3">
                      <p className="text-sm font-medium text-red-800">
                        이전 프로모션 코드 요청이 반려되었습니다.
                      </p>
                      <p className="text-sm text-red-700 mt-1">
                        다시 요청하실 수 있습니다.
                      </p>
                    </div>
                    <button
                      onClick={() => setShowPromoRequest(true)}
                      className="relative w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-bold py-3 px-6 rounded-lg transition-all transform hover:scale-105 shadow-lg flex items-center justify-center gap-2"
                    >
                      <Gift className="w-5 h-5" />
                      프로모션 코드 다시 요청하기
                    </button>
                  </>
                ) : (
                  <button
                  onClick={() => setShowPromoRequest(true)}
                  className="relative w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-bold py-3 px-6 rounded-lg transition-all transform hover:scale-105 shadow-lg flex items-center justify-center gap-2 animate-pulse"
                  >
                  <Gift className="w-5 h-5" />
                  ₩29,700 무료 프로모션 코드 요청하기
                    </button>
                )}
                
                {promoRequestMessage.text && (
                  <div className={`mt-2 text-sm ${
                    promoRequestMessage.type === 'success' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {promoRequestMessage.text}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 활성 프로모션 코드들 */}
        {activePromoCodes.length > 0 && (
          <div className="card bg-gradient-to-r from-amber-50 to-orange-50 border-orange-200">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Tag className="w-5 h-5 text-orange-600" />
              활성 프로모션 코드
            </h3>
            
            <div className="space-y-4">
              {activePromoCodes.map((promoCode) => (
                <div key={promoCode.id} className="border-b border-orange-100 last:border-b-0 pb-3 last:pb-0">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">코드</span>
                      <code className="font-mono bg-white px-3 py-1 rounded-md text-sm">
                        {promoCode.promo_codes?.code || promoCode.code}
                      </code>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">타입</span>
                      <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800">
                        {promoCode.promo_codes?.type || promoCode.type}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">혜택</span>
                      <span className="text-sm text-gray-800">
                        {getBenefitText(promoCode)}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">혜택 만료일</span>
                      <span className="text-sm text-gray-800">
                        {promoCode.expires_at ? formatDate(promoCode.expires_at) : '무기한'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 프로모션 코드 요청 확인 모달 */}
      {showPromoRequest && (
        <div className="modal-overlay">
          <div className="modal modal-lg max-h-[90vh] overflow-hidden">
            {/* 헤더: 아이콘 + 타이틀 + 닫기 버튼 */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 bg-amber-50">
                  <Send className="w-6 h-6 text-amber-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                  프로모션 코드 요청
                </h3>
              </div>
              <button
                onClick={() => {
                  setShowPromoRequest(false);
                  setPromoRequestForm({
                    gender: '',
                    ageGroup: '',
                    usageReasons: [],
                    otherReason: '',
                    discoveryPath: '',
                    otherPath: ''
                  });
                  setFormErrors({});
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            {/* 스크롤 가능한 본문 */}
            <div className="overflow-y-auto max-h-[calc(90vh-200px)] pr-2">
            <div className="space-y-6">
              {/* 프로모션 혜택 강조 */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-4 border border-amber-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-amber-900 flex items-center gap-2">
                    <Gift className="h-5 w-5 text-amber-600" />
                    3개월 무료 프로모션 혜택
                  </h4>
                  <div className="bg-red-500 text-white px-3 py-1 rounded-full">
                    <span className="font-bold text-sm">₩29,700 절약!</span>
                  </div>
                </div>
                <div className="bg-white/70 rounded-lg p-2 mb-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Pro 플랜 정상가 (3개월)</span>
                    <span className="line-through text-gray-500">₩29,700</span>
                  </div>
                  <div className="flex items-center justify-between text-sm font-bold mt-1">
                    <span className="text-amber-900">프로모션 특별가</span>
                    <span className="text-green-600 text-lg">₩0</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-amber-800">
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    <span>AI 이력서 무제한 생성</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    <span>이력서 PDF 다운로드</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    <span>영문 이력서 자동 생성</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    <span>개인 포트폴리오 웹사이트 생성</span>
                  </div>
                </div>
              </div>
              
              <p className="text-gray-600">
                간단한 정보만 입력하시면 빠르게 승인해드립니다!
              </p>
              
              {/* 성별 */}
              <div>
                <label className="label">성별 <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-3 gap-2 max-w-md">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="gender"
                      value="남성"
                      checked={promoRequestForm.gender === '남성'}
                      onChange={(e) => setPromoRequestForm(prev => ({ ...prev, gender: e.target.value }))}
                      className="mr-2"
                    />
                    남성
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="gender"
                      value="여성"
                      checked={promoRequestForm.gender === '여성'}
                      onChange={(e) => setPromoRequestForm(prev => ({ ...prev, gender: e.target.value }))}
                      className="mr-2"
                    />
                    여성
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="gender"
                      value="기타"
                      checked={promoRequestForm.gender === '기타'}
                      onChange={(e) => setPromoRequestForm(prev => ({ ...prev, gender: e.target.value }))}
                      className="mr-2"
                    />
                    기타
                  </label>
                </div>
                {formErrors.gender && <p className="text-red-500 text-sm mt-1">{formErrors.gender}</p>}
              </div>
              
              {/* 연령대 */}
              <div>
                <label className="label">연령대 <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-3 gap-2 max-w-md">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="ageGroup"
                      value="20대"
                      checked={promoRequestForm.ageGroup === '20대'}
                      onChange={(e) => setPromoRequestForm(prev => ({ ...prev, ageGroup: e.target.value }))}
                      className="mr-2"
                    />
                    20대
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="ageGroup"
                      value="30대"
                      checked={promoRequestForm.ageGroup === '30대'}
                      onChange={(e) => setPromoRequestForm(prev => ({ ...prev, ageGroup: e.target.value }))}
                      className="mr-2"
                    />
                    30대
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="ageGroup"
                      value="40대"
                      checked={promoRequestForm.ageGroup === '40대'}
                      onChange={(e) => setPromoRequestForm(prev => ({ ...prev, ageGroup: e.target.value }))}
                      className="mr-2"
                    />
                    40대
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="ageGroup"
                      value="50대"
                      checked={promoRequestForm.ageGroup === '50대'}
                      onChange={(e) => setPromoRequestForm(prev => ({ ...prev, ageGroup: e.target.value }))}
                      className="mr-2"
                    />
                    50대
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="ageGroup"
                      value="60대 이상"
                      checked={promoRequestForm.ageGroup === '60대 이상'}
                      onChange={(e) => setPromoRequestForm(prev => ({ ...prev, ageGroup: e.target.value }))}
                      className="mr-2"
                    />
                    60대 이상
                  </label>
                </div>
                {formErrors.ageGroup && <p className="text-red-500 text-sm mt-1">{formErrors.ageGroup}</p>}
              </div>
              
              {/* 사용 목적 */}
              <div>
                <label className="label">라떼이즈를 사용하려는 이유 <span className="text-red-500">*</span></label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={promoRequestForm.usageReasons.includes('이력서 만들기')}
                      onChange={() => handleUsageReasonChange('이력서 만들기')}
                      className="mr-2"
                    />
                    이력서 만들기 (+영문 이력서 생성)
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={promoRequestForm.usageReasons.includes('개인 웹 페이지 만들기')}
                      onChange={() => handleUsageReasonChange('개인 웹 페이지 만들기')}
                      className="mr-2"
                    />
                    개인 웹 페이지 만들기
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={promoRequestForm.usageReasons.includes('지식 블로그 만들기')}
                      onChange={() => handleUsageReasonChange('지식 블로그 만들기')}
                      className="mr-2"
                    />
                    지식 블로그 만들기
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={promoRequestForm.usageReasons.includes('기타')}
                      onChange={() => handleUsageReasonChange('기타')}
                      className="mr-2"
                    />
                    <span>기타:</span>
                    <input
                      type="text"
                      value={promoRequestForm.otherReason}
                      onChange={(e) => setPromoRequestForm(prev => ({ ...prev, otherReason: e.target.value }))}
                      placeholder="직접 입력"
                      className="input px-2 py-1 text-sm ml-2 flex-1 max-w-md"
                      disabled={!promoRequestForm.usageReasons.includes('기타')}
                    />
                  </label>
                </div>
                {formErrors.usageReasons && <p className="text-red-500 text-sm mt-1">{formErrors.usageReasons}</p>}
                {formErrors.otherReason && <p className="text-red-500 text-sm mt-1">{formErrors.otherReason}</p>}
              </div>
              
              {/* 알게 된 경로 */}
              <div>
                <label className="label">라떼이즈를 알게 된 경로 <span className="text-red-500">*</span></label>
                <div className="space-y-2">
                  <div className="grid grid-cols-3 gap-2 max-w-md">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="discoveryPath"
                        value="지인 추천"
                        checked={promoRequestForm.discoveryPath === '지인 추천'}
                        onChange={(e) => setPromoRequestForm(prev => ({ ...prev, discoveryPath: e.target.value }))}
                        className="mr-2"
                      />
                      지인 추천
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="discoveryPath"
                        value="검색"
                        checked={promoRequestForm.discoveryPath === '검색'}
                        onChange={(e) => setPromoRequestForm(prev => ({ ...prev, discoveryPath: e.target.value }))}
                        className="mr-2"
                      />
                      검색
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="discoveryPath"
                        value="광고/홍보"
                        checked={promoRequestForm.discoveryPath === '광고/홍보'}
                        onChange={(e) => setPromoRequestForm(prev => ({ ...prev, discoveryPath: e.target.value }))}
                        className="mr-2"
                      />
                      광고/홍보
                    </label>
                  </div>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="discoveryPath"
                      value="기타"
                      checked={promoRequestForm.discoveryPath === '기타'}
                      onChange={(e) => setPromoRequestForm(prev => ({ ...prev, discoveryPath: e.target.value }))}
                      className="mr-2"
                    />
                    <span>기타:</span>
                    <input
                      type="text"
                      value={promoRequestForm.otherPath}
                      onChange={(e) => setPromoRequestForm(prev => ({ ...prev, otherPath: e.target.value }))}
                      placeholder="직접 입력"
                      className="input px-2 py-1 text-sm ml-2 flex-1 max-w-md"
                      disabled={promoRequestForm.discoveryPath !== '기타'}
                    />
                  </label>
                </div>
                {formErrors.discoveryPath && <p className="text-red-500 text-sm mt-1">{formErrors.discoveryPath}</p>}
                {formErrors.otherPath && <p className="text-red-500 text-sm mt-1">{formErrors.otherPath}</p>}
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>안내:</strong>
                </p>
                <ul className="text-sm text-blue-700 mt-2 space-y-1">
                  <li>• 관리자가 요청을 검토 후 승인합니다.</li>
                  <li>• 승인 후 등록된 이메일로 코드가 발송됩니다.</li>
                  <li>• 이메일: <strong>{user?.email}</strong></li>
                </ul>
              </div>
              
              {promoRequestMessage.text && (
                <div className={`text-sm ${
                  promoRequestMessage.type === 'success' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {promoRequestMessage.text}
                </div>
              )}  
            </div>
            </div>
            
            {/* 하단 버튼 - 고정 위치 */}
            <div className="flex gap-3 mt-6 pt-4 border-t">
              <button
                onClick={() => {
                  setShowPromoRequest(false);
                  setPromoRequestForm({
                    gender: '',
                    ageGroup: '',
                    usageReasons: [],
                    otherReason: '',
                    discoveryPath: '',
                    otherPath: ''
                  });
                  setFormErrors({});
                }}
                className="btn btn-ghost btn-md flex-1"
                disabled={promoRequestLoading}
              >
                취소
              </button>
              <button
                onClick={handlePromoRequestSubmit}
                disabled={promoRequestLoading || !isFormValid()}
                className="btn btn-primary btn-md flex-1"
              >
                {promoRequestLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                요청
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PromoCodeSection;
